import type { ApiClient } from "@ncp/api-client";

export type RedraftFormat = "markdown" | "plain";

/** Where the text sits, so the assistant writes something that fits: a finding on a criterion, an AI draft, a rationale, a report passage. */
export interface RedraftContext {
  caseId?: string | null;
  kind?: string;
  title?: string;
  guidance?: string;
  verdict?: string;
  platformAssessment?: string;
  /** A counterpart text the result should stay aligned with — the English reasoning while the Arabic one is written, for instance. */
  reference?: string;
}

export interface RedraftInput {
  text: string;
  instruction: string;
  locale: string;
  format: RedraftFormat;
  context?: RedraftContext;
}

export interface RedraftResult {
  text: string;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  latencyMs: number;
  offline: boolean;
  flags: string[];
}

type RedraftEvent = { type: "token"; text: string } | { type: "final"; result: RedraftResult } | { type: "error"; message: string };

/**
 * Streams a redraft: `onToken` receives the text written so far, the promise resolves with the verified result. Falls back to the
 * non-streaming endpoint when the stream ends without a final event (a proxy that buffers, for example).
 */
export async function redraftText(api: ApiClient, input: RedraftInput, onToken: (soFar: string) => void, signal?: AbortSignal): Promise<RedraftResult> {
  let streamed = "";
  let final: RedraftResult | null = null;
  for await (const event of api.streamEvents<RedraftEvent>("/api/ai/redraft/stream", input, signal)) {
    if (event.type === "token") { streamed += event.text; onToken(streamed); }
    else if (event.type === "final") final = event.result;
    else throw new Error(event.message);
  }
  if (final) return final;
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  return api.post<RedraftResult>("/api/ai/redraft", input, { signal });
}
